import React, { useState, useRef } from "react";
import { Icon, Menu, Button } from "@showitinc/showit-wc";
import styles from "./SiteFolderDropdown.module.css";
import SiteFolderConstants from "@/constants/SiteFolderDefaultConstants";
import { sortFoldersAlphabetically } from "@/utils/siteFolderSortingUtil";
import cx from "classnames";

const { UNCATEGORIZED, ADD_NEW_FOLDER, UNCATEGORIZED_FOLDER_ID, ADD_NEW_FOLDER_ID } =
  SiteFolderConstants;

const AssignmentMenuItem = ({
  option,
  isSelected,
  onClick,
  hasSelection = false,
  hasUnsavedChanges,
  iconSize = "20",
}) => {
  const { label, iconType, isAddNew = false, hasAssigned = false } = option;
  const shouldShowDash = hasAssigned && !hasUnsavedChanges;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cx(styles["menu-item"], {
        [styles.selected]: isSelected,
        [styles["add-new"]]: isAddNew,
      })}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {!isAddNew ? (
        <div
          className={cx(styles["radio-button"], {
            [styles.selected]: isSelected,
            [styles["has-assigned"]]: shouldShowDash,
            [styles.unselected]: hasSelection && !isSelected,
          })}
        ></div>
      ) : (
        <div className={styles["radio-placeholder"]}></div>
      )}
      <div
        className={cx(styles.icon, {
          [styles["folder-icon"]]: iconType === "folderClosed",
        })}
      >
        <Icon type={iconType} size={iconSize} />
      </div>
      <span
        className={cx(styles["folder-name"], {
          [styles.uncategorized]: label === "Uncategorized",
        })}
      >
        {label}
      </span>
    </div>
  );
};

/**
 * SiteFolderDropdown component - Dropdown for folder assignment/management
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of folder options
 * @param {*} props.selectedValue - Selected value
 * @param {Function} props.onSelect - On select callback
 * @param {string} [props.placeholder] - Placeholder text
 * @param {Function} [props.onAddFolder] - On add folder callback
 * @param {Function} [props.onSave] - On save callback
 * @param {Function} [props.onCancel] - On cancel callback
 * @param {boolean} [props.showActionButtons] - Whether to show action buttons
 * @param {boolean} [props.disableSaveButton] - Whether to disable the save button
 * @param {string} [props.className] - Additional CSS class name
 * @returns {JSX.Element} The dropdown component
 */
const SiteFolderDropdown = ({
  options = [],
  selectedValue = null,
  onSelect = () => {},
  placeholder = "Select...",
  onAddFolder = () => {},
  onSave = () => {},
  onCancel = () => {},
  showActionButtons = false,
  disableSaveButton = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const menuRef = useRef(null);

  const handleOptionSelect = (option) => {
    if (option.value === selectedValue) {
      return;
    }
    onSelect(option);
    setHasUnsavedChanges(true);
  };

  const handleAddFolder = () => {
    menuRef.current?.hidePopover();
    onAddFolder?.();
  };

  const handleSave = () => {
    onSave();
    setHasUnsavedChanges(false);
    menuRef.current?.hidePopover();
  };

  const handleCancel = () => {
    onCancel();
    setHasUnsavedChanges(false);
    menuRef.current?.hidePopover();
  };

  const handleMenuClose = () => {
    if (hasUnsavedChanges) {
      onCancel();
      setHasUnsavedChanges(false);
    }
    setIsOpen(false);
  };

  const handleMenuOpen = () => {
    setIsOpen(true);
  };

  const allOptions = [...options, UNCATEGORIZED, ADD_NEW_FOLDER];
  const selectedOption = allOptions.find((option) => option.value === selectedValue);

  return (
    <Menu
      ref={menuRef}
      closeType="auto"
      position="bottom"
      onSi-open={handleMenuOpen}
      onSi-close={handleMenuClose}
      py="sm"
      min-parent
      max-parent
    >
      <div
        slot="trigger-element"
        className={cx(styles["dropdown-trigger"], styles["dropdown-trigger-assignment"])}
      >
        <div className={styles["dropdown-trigger-content"]}>
          <div className={styles["folder-icon"]}>
            <Icon type="folderClosed" size="20" />
          </div>
          <span>{selectedOption ? selectedOption.label : placeholder}</span>
        </div>
        <div className={cx(styles["dropdown-arrow"], { [styles.open]: isOpen })}>
          <Icon type="arrowChevronDown" />
        </div>
      </div>

      <div className={styles["menu-content"]}>
        {sortFoldersAlphabetically(
          options.filter((option) => option.label !== "Uncategorized"),
        ).map((option, index) => (
          <AssignmentMenuItem
            key={option.value || index}
            option={option}
            isSelected={selectedValue === option.value}
            hasSelection={!!selectedValue}
            hasUnsavedChanges={hasUnsavedChanges}
            onClick={() => handleOptionSelect(option)}
          />
        ))}

        {options.filter((option) => option.label !== "Uncategorized").length > 0 && (
          <AssignmentMenuItem
            option={UNCATEGORIZED}
            isSelected={selectedValue === UNCATEGORIZED_FOLDER_ID}
            hasSelection={!!selectedValue}
            hasUnsavedChanges={hasUnsavedChanges}
            onClick={() => handleOptionSelect(UNCATEGORIZED)}
          />
        )}

        {options.length > 0 && <div className={styles["menu-divider"]}></div>}
        <AssignmentMenuItem
          option={ADD_NEW_FOLDER}
          isSelected={selectedValue === ADD_NEW_FOLDER_ID}
          hasSelection={!!selectedValue}
          hasUnsavedChanges={hasUnsavedChanges}
          onClick={handleAddFolder}
          iconSize="16"
        />

        {showActionButtons && (
          <div className={styles["action-buttons"]}>
            <Button
              variant="feature"
              disabled={!hasUnsavedChanges || disableSaveButton}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
        )}
      </div>
    </Menu>
  );
};

export default SiteFolderDropdown;
